import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, X, Filter, CheckCircle, Clock, DollarSign, MessageCircle, ChevronDown, ChevronRight, Wand2, Star, BarChart4, PieChart, Calendar, Tags, Users, Gift, Sparkles, BadgeCheck, Zap, Trophy } from "lucide-react";
import NavBar from "../components/NavBar";
import { getMyGenies } from "../services/offerService";
import { toast } from "sonner";
import { authenticatedGet, authenticatedPut } from "../utils/apiUtils";
import { getFullProfileImageUrl } from "../contexts/ProfileContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import ChatButton from '../components/ChatButton';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import '../styles/dashboard.css';

interface Will {
  currency: string | null;
}

interface Offer {
  id: string;
  willId: string;
  willTitle: string;
  description: string;
  providerId: string;
  providerName: string;
  providerContactInfo: string;
  providerProfilePicture?: string | null;
  status: string;
  price: number;
  currency: string | null;
  additionalDetails: string | null;
  estimatedCompletionDate: string;
  offererId: string | null;
  createdAt: string;
  updatedAt: string;
  will?: Will;
}

// Statistics interface
interface DashboardStats {
  totalOffers: number;
  pendingOffers: number;
  inProgressOffers: number;
  completedOffers: number;
  rejectedOffers: number;
  averagePrice: number;
  totalBudget: number;
  mostPopularWant: string | null;
}

// Group of offers by want
interface GroupedOffers {
  id: string;
  title: string;
  offers: Offer[];
}

// Want menu item
interface WantMenuItem {
  id: string;
  title: string;
  offersCount: number;
}

const MyGenies = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("newest");
  const [viewMode, setViewMode] = useState<"active" | "archived">("active");
  const userId = localStorage.getItem('userId');
  const navigate = useNavigate();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [selectedWillId, setSelectedWillId] = useState<string | null>(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalOffers: 0,
    pendingOffers: 0,
    inProgressOffers: 0,
    completedOffers: 0,
    rejectedOffers: 0,
    averagePrice: 0,
    totalBudget: 0,
    mostPopularWant: null
  });
  const [groupedOffers, setGroupedOffers] = useState<GroupedOffers[]>([]);
  const [expandedWants, setExpandedWants] = useState<Set<string>>(new Set());
  const [wantMenuItems, setWantMenuItems] = useState<WantMenuItem[]>([]);
  const [selectedOfferForView, setSelectedOfferForView] = useState<Offer | null>(null);
  const [showOfferDetailsDialog, setShowOfferDetailsDialog] = useState(false);
  const [providerProfiles, setProviderProfiles] = useState<Map<string, { profilePicture?: string | null }>>(new Map());

  const calculateDashboardStats = (offersData: Offer[]) => {
    // Count offers by status
    const pendingOffers = offersData.filter(offer => offer.status === "PENDING").length;
    const inProgressOffers = offersData.filter(offer => offer.status === "IN_PROGRESS").length;
    const completedOffers = offersData.filter(offer => offer.status === "COMPLETED").length;
    const rejectedOffers = offersData.filter(offer => offer.status === "REJECTED").length;

    // Calculate average price
    const totalPrice = offersData.reduce((sum, offer) => sum + offer.price, 0);
    const averagePrice = offersData.length > 0 ? totalPrice / offersData.length : 0;

    // Find most popular want
    const willCounts = new Map<string, number>();
    offersData.forEach(offer => {
      const currentCount = willCounts.get(offer.willTitle) || 0;
      willCounts.set(offer.willTitle, currentCount + 1);
    });

    let mostPopularWant: string | null = null;
    let maxCount = 0;

    willCounts.forEach((count, title) => {
      if (count > maxCount) {
        maxCount = count;
        mostPopularWant = title;
      }
    });

    setDashboardStats({
      totalOffers: offersData.length,
      pendingOffers,
      inProgressOffers,
      completedOffers,
      rejectedOffers,
      averagePrice,
      totalBudget: totalPrice,
      mostPopularWant
    });
  };

  const generateWantMenuItems = (offersData: Offer[]) => {
    const uniqueWills = new Map<string, WantMenuItem>();

    offersData.forEach(offer => {
      if (!uniqueWills.has(offer.willId)) {
        uniqueWills.set(offer.willId, {
          id: offer.willId,
          title: offer.willTitle,
          offersCount: 1
        });
      } else {
        const current = uniqueWills.get(offer.willId)!;
        uniqueWills.set(offer.willId, {
          ...current,
          offersCount: current.offersCount + 1
        });
      }
    });

    // Convert map to array and sort by title
    const sortedWants = Array.from(uniqueWills.values()).sort((a, b) => a.title.localeCompare(b.title));
    setWantMenuItems(sortedWants);
  };

  const groupOffersByWant = (offersData: Offer[]) => {
    // Apply status filter if it's not "all"
    let filtered = statusFilter === "all"
      ? offersData
      : offersData.filter(offer => offer.status === statusFilter);

    // Apply want filter if selected
    if (selectedWillId) {
      filtered = filtered.filter(offer => offer.willId === selectedWillId);
    }

    // Group offers by willId
    const groups = new Map<string, GroupedOffers>();

    filtered.forEach(offer => {
      if (!groups.has(offer.willId)) {
        groups.set(offer.willId, {
          id: offer.willId,
          title: offer.willTitle,
          offers: []
        });
      }

      const group = groups.get(offer.willId);
      if (group) {
        group.offers.push(offer);
      }
    });

    // Sort the offers within each group
    groups.forEach(group => {
      group.offers.sort((a, b) => {
        switch (sortBy) {
          case "newest": return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          case "oldest": return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          case "price-high": return b.price - a.price;
          case "price-low": return a.price - b.price;
          default: return 0;
        }
      });
    });

    // Convert map to array and sort groups by title
    const sortedGroups = Array.from(groups.values()).sort((a, b) => a.title.localeCompare(b.title));

    setGroupedOffers(sortedGroups);
  };

  // New function to fetch provider profile pictures if not already in the offers
  const fetchProviderProfiles = async (offersData: Offer[]) => {
    const uniqueProviderIds = [...new Set(offersData.map(offer => offer.providerId))];
    const newProviderProfiles = new Map(providerProfiles);
    
    // Only fetch for providers we don't already have
    const providersToFetch = uniqueProviderIds.filter(id => !newProviderProfiles.has(id));
    
    if (providersToFetch.length === 0) return;
    
    try {
      await Promise.all(
        providersToFetch.map(async (providerId) => {
          const response = await authenticatedGet(`${import.meta.env.VITE_API_URL}/profiles/${providerId}`);
          if (response.ok) {
            const data = await response.json();
            newProviderProfiles.set(providerId, {
              profilePicture: data.profilePictureUrl || data.profileImageUrl
            });
          }
        })
      );
      
      setProviderProfiles(newProviderProfiles);
    } catch (error) {
      console.error("Error fetching provider profiles:", error);
    }
  };

  const loadOffers = async () => {
    try {
      setLoading(true);

      // Use the appropriate endpoint based on viewMode
      const endpoint = viewMode === "active" 
        ? `${import.meta.env.VITE_API_URL}/offers/received/active` 
        : `${import.meta.env.VITE_API_URL}/offers/received/archived`;
      
      const response = await authenticatedGet(endpoint);
      
      if (!response.ok) {
        throw new Error('Failed to load offers');
      }
      
      const data = await response.json();

      // Handle both paginated and non-paginated responses
      const offersData = data.content ? data.content : data;
      
      setOffers(offersData);
      calculateDashboardStats(offersData);
      groupOffersByWant(offersData);
      generateWantMenuItems(offersData);
      
      // Fetch provider profiles
      fetchProviderProfiles(offersData);
    } catch (error) {
      console.error('Failed to load offers:', error);
      toast.error("Failed to load your genies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userId) {
      navigate('/signin');
      return;
    }
    loadOffers();
  }, [userId, navigate, viewMode]);

  useEffect(() => {
    // Initialize all wants as expanded
    if (groupedOffers.length > 0) {
      const allWantIds = new Set(groupedOffers.map(group => group.id));
      setExpandedWants(allWantIds);
    }
  }, [groupedOffers]);

  useEffect(() => {
    groupOffersByWant(offers);
  }, [statusFilter, sortBy, selectedWillId, viewMode]);

  const handleStatusUpdate = async (offerId: string, newStatus: string) => {
    try {
      const response = await authenticatedPut(
        `${import.meta.env.VITE_API_URL}/offers/${offerId}/status`,
        { status: newStatus }
      );

      if (!response.ok) {
        throw new Error('Failed to update offer status');
      }

      const updatedOffers = offers.map(offer =>
        offer.id === offerId ? { ...offer, status: newStatus } : offer
      );

      setOffers(updatedOffers);
      calculateDashboardStats(updatedOffers);
      groupOffersByWant(updatedOffers);
      generateWantMenuItems(updatedOffers);

      toast.success(`Offer ${newStatus.toLowerCase()} successfully`);
    } catch (error) {
      console.error('Error updating offer status:', error);
      toast.error('Failed to update offer status');
    }
  };

  const statusIcons = {
    PENDING: <Clock className="h-4 w-4" />,
    IN_REVIEW: <Users className="h-4 w-4" />,
    IN_PROGRESS: <Zap className="h-4 w-4" />,
    COMPLETED: <BadgeCheck className="h-4 w-4" />,
    REJECTED: <X className="h-4 w-4" />,
    CANCELLED: <X className="h-4 w-4" />
  };

  const getCurrencySymbol = (currency: string | null): string => {
    const currencySymbols: { [key: string]: string } = {
      USD: '$',
      EUR: '€',
      ILS: '₪',
      GBP: '£'
    };
    return currencySymbols[currency || 'ILS'] || '₪';
  };

  const handleRejectClick = (e: React.MouseEvent, offerId: string) => {
    e.stopPropagation();
    setSelectedOfferId(offerId);
    setShowRejectDialog(true);
  };

  const getProgressValue = (status: string): number => {
    switch (status) {
      case 'PENDING': return 25;
      case 'IN_REVIEW': return 50;
      case 'IN_PROGRESS': return 75;
      case 'COMPLETED': return 100;
      case 'PAID': return 100;
      default: return 0;
    }
  };

  const toggleWantExpansion = (wantId: string) => {
    const newExpandedWants = new Set(expandedWants);
    if (newExpandedWants.has(wantId)) {
      newExpandedWants.delete(wantId);
    } else {
      newExpandedWants.add(wantId);
    }
    setExpandedWants(newExpandedWants);
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const openOfferDetails = (offer: Offer) => {
    setSelectedOfferForView(offer);
    setShowOfferDetailsDialog(true);
  };

  // Get provider profile picture
  const getProviderProfilePicture = (providerId: string, offer: Offer) => {
    // First check if the offer already has the profile picture
    if (offer.providerProfilePicture) {
      return getFullProfileImageUrl(offer.providerProfilePicture);
    }
    
    // Then check our cached provider profiles
    const providerProfile = providerProfiles.get(providerId);
    if (providerProfile?.profilePicture) {
      return getFullProfileImageUrl(providerProfile.profilePicture);
    }
    
    // Return null if no profile picture is found
    return null;
  };

  return (
    <div className="min-h-screen dashboard-gradient">
      <NavBar />
      <main className="container mx-auto px-4 pt-24 pb-12 min-h-[calc(100vh-80px)]">
        <div className="flex flex-col space-y-6">
          {/* Dashboard Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-heading">My Genies</h1>
              <p className="text-gray-500 mt-1">
                Track and manage the offers from service providers
              </p>
            </div>

            {/* View mode toggle and filters */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
                <button
                  onClick={() => setViewMode("active")}
                  className={`flex-1 sm:flex-auto px-4 py-2 rounded-md transition-colors ${
                    viewMode === "active"
                      ? "bg-white shadow-sm text-indigo-600"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setViewMode("archived")}
                  className={`flex-1 sm:flex-auto px-4 py-2 rounded-md transition-colors ${
                    viewMode === "archived"
                      ? "bg-white shadow-sm text-indigo-600"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Archive
                </button>
              </div>

              <div className="flex flex-wrap w-full sm:w-auto gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[160px] bg-white border-gray-200 luxury-input">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All Statuses</SelectItem>
                    {viewMode === "active" ? (
                      <>
                        <SelectItem value="PENDING">Pending</SelectItem>
                        <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="COMPLETED">Completed</SelectItem>
                        <SelectItem value="REJECTED">Rejected</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full sm:w-[160px] bg-white border-gray-200 luxury-input">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Dashboard Statistics - Hidden on mobile, visible on md screens and up */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {viewMode === "active" ? (
              <>
                <Card className="luxury-card">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center">
                        <Wand2 className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div>
                        <div className="text-sm text-muted">Active Offers</div>
                        <div className="text-2xl font-bold text-heading">
                          {dashboardStats.pendingOffers + dashboardStats.inProgressOffers}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="luxury-card">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-amber-50 flex items-center justify-center">
                        <Clock className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <div className="text-sm text-muted">Pending</div>
                        <div className="text-2xl font-bold text-heading">{dashboardStats.pendingOffers}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="luxury-card">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center">
                        <Zap className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-sm text-muted">In Progress</div>
                        <div className="text-2xl font-bold text-heading">{dashboardStats.inProgressOffers}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="luxury-card">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full gold-accent flex items-center justify-center">
                        <DollarSign className="h-6 w-6 text-amber-900" />
                      </div>
                      <div>
                        <div className="text-sm text-muted">Average Price</div>
                        <div className="text-2xl font-bold text-heading">
                          {getCurrencySymbol('ILS')}{Math.round(dashboardStats.averagePrice)}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                <Card className="luxury-card">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center">
                        <Wand2 className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div>
                        <div className="text-sm text-muted">Archived Offers</div>
                        <div className="text-2xl font-bold text-heading">
                          {dashboardStats.completedOffers + dashboardStats.rejectedOffers}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="luxury-card">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center">
                        <BadgeCheck className="h-6 w-6 text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-sm text-muted">Completed</div>
                        <div className="text-2xl font-bold text-heading">{dashboardStats.completedOffers}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="luxury-card">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-rose-50 flex items-center justify-center">
                        <X className="h-6 w-6 text-rose-600" />
                      </div>
                      <div>
                        <div className="text-sm text-muted">Rejected</div>
                        <div className="text-2xl font-bold text-heading">{dashboardStats.rejectedOffers}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="luxury-card">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full gold-accent flex items-center justify-center">
                        <DollarSign className="h-6 w-6 text-amber-900" />
                      </div>
                      <div>
                        <div className="text-sm text-muted">Total Budget</div>
                        <div className="text-2xl font-bold text-heading">
                          {getCurrencySymbol('ILS')}{Math.round(dashboardStats.totalBudget)}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Mobile Sidebar Toggle */}
          <div className="lg:hidden">
            <Button
              variant="outline"
              className="rounded-md px-4 flex items-center gap-2 bg-white border-gray-200 hover:bg-gray-50"
              onClick={toggleSidebar}
            >
              <Filter className="h-4 w-4 text-gray-600" />
              <span>Filter by Wants</span>
            </Button>
          </div>

          {/* Main Content with Sidebar */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar - Want Filter */}
            <div className={`lg:w-72 xl:w-80 space-y-4 ${showSidebar ? 'block' : 'hidden lg:block'}`}>
              {/* Wants Filter */}
              <Card className="sticky top-24 luxury-card">
                <CardHeader className="pb-2 border-b border-gray-100">
                  <CardTitle className="text-base font-medium text-heading">My Wants</CardTitle>
                </CardHeader>
                <CardContent className="p-4 max-h-[70vh] overflow-y-auto">
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setSelectedWillId(null);
                        setShowSidebar(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md transition-colors ${!selectedWillId
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                        }`}
                    >
                      All Wants
                    </button>
                    {wantMenuItems.map(want => (
                      <button
                        key={want.id}
                        onClick={() => {
                          setSelectedWillId(want.id);
                          setShowSidebar(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md transition-colors ${selectedWillId === want.id
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                          }`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="truncate">{want.title}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${selectedWillId === want.id ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-600'
                            }`}>
                            {want.offersCount}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content - Grouped by Wants */}
            <div className="flex-grow space-y-6">
              {/* Filtered Results Header */}
              {/* <div className="flex justify-between items-center mb-2">
                <div>
                  <h2 className="text-xl font-semibold text-heading">
                    {selectedWillId
                      ? `Offers for: ${wantMenuItems.find(w => w.id === selectedWillId)?.title}`
                      : statusFilter === "all"
                        ? "All Offers"
                        : `${statusFilter.replace('_', ' ')} Offers`}
                  </h2>
                  <p className="text-sm text-muted mt-1">
                    {offers.filter(o => !selectedWillId || o.willId === selectedWillId)
                      .filter(o => statusFilter === "all" || o.status === statusFilter).length} results
                  </p>
                </div>
              </div> */}

              {/* Main Content */}
              {loading ? (
                <div className="luxury-card p-8 flex justify-center items-center">
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                    <p className="mt-4 text-muted">Loading your genie offers...</p>
                  </div>
                </div>
              ) : groupedOffers.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">
                    <Wand2 className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-medium text-heading mb-2">No offers found</h3>
                  <p className="text-muted max-w-md mx-auto">
                    {viewMode === "active" 
                      ? "No active offers found. Check the archive for completed or rejected offers."
                      : "No archived offers found. Complete or reject offers to see them here."}
                  </p>
                </div>
              ) : (
                groupedOffers.map((group) => (
                  <Card key={group.id} className="luxury-card shadow-sm overflow-hidden">
                    <CardHeader className="pb-3 border-b border-gray-100 bg-gray-50/50">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 h-auto"
                            onClick={() => toggleWantExpansion(group.id)}
                          >
                            {expandedWants.has(group.id) ?
                              <ChevronDown className="h-5 w-5 text-gray-600" /> :
                              <ChevronRight className="h-5 w-5 text-gray-600" />
                            }
                          </Button>
                          <CardTitle className="text-lg font-semibold text-heading flex items-center gap-2">
                            <span>{group.title}</span>
                            <Badge className="ml-2 bg-indigo-100 text-indigo-700 hover:bg-indigo-200">
                              {group.offers.length} {group.offers.length === 1 ? 'offer' : 'offers'}
                            </Badge>
                          </CardTitle>
                        </div>
                        {/* Hide detailed stats on mobile */}
                        <div className="hidden md:flex items-center gap-2 text-sm text-muted">
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {group.offers.filter(o => o.status === "PENDING").length} pending
                          </span>
                          <span className="flex items-center gap-1">
                            <Zap className="h-4 w-4" />
                            {group.offers.filter(o => o.status === "IN_PROGRESS").length} in progress
                          </span>
                          <span className="flex items-center gap-1">
                            <BadgeCheck className="h-4 w-4" />
                            {group.offers.filter(o => o.status === "COMPLETED").length} completed
                          </span>
                        </div>
                      </div>
                    </CardHeader>

                    {expandedWants.has(group.id) && (
                      <CardContent className="p-0">
                        <div className="divide-y divide-gray-100">
                          {group.offers.map((offer) => (
                            <div
                              key={offer.id}
                              className="p-4 sm:p-5 hover:bg-gray-50/50 transition-colors"
                            >
                              <div className="flex flex-col lg:flex-row justify-between gap-4 lg:gap-6">
                                {/* Left Column - Provider & Status */}
                                <div className="lg:w-1/4 space-y-4">
                                  <div className="flex flex-row sm:flex-col justify-between sm:justify-start items-center sm:items-start space-y-0 sm:space-y-3">
                                    {/* Provider Info */}
                                    <div className="flex items-center gap-3">
                                      <Link to={`/profile/${offer.providerId}`} className="hover:opacity-80 transition-opacity">
                                        <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border-2 border-transparent hover:border-indigo-200 transition-all">
                                          {getProviderProfilePicture(offer.providerId, offer) ? (
                                            <AvatarImage 
                                              src={getProviderProfilePicture(offer.providerId, offer) || ''} 
                                              alt={offer.providerName} 
                                            />
                                          ) : (
                                            <AvatarFallback className="bg-indigo-100 text-indigo-600">
                                              {offer.providerName.charAt(0)}
                                            </AvatarFallback>
                                          )}
                                        </Avatar>
                                      </Link>
                                      <div>
                                        <Link 
                                          to={`/profile/${offer.providerId}`} 
                                          className="font-medium text-heading hover:text-indigo-600 hover:underline transition-colors"
                                        >
                                          {offer.providerName}
                                        </Link>
                                        <div className="text-xs sm:text-sm text-muted">{offer.providerContactInfo}</div>
                                      </div>
                                    </div>
                                    {/* Received Date */}
                                    <div className="text-xs text-muted">
                                      Received {new Date(offer.createdAt).toLocaleDateString(undefined, {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                    </div>
                                  </div>

                                  {/* Chat Button */}
                                  <ChatButton
                                    userId={offer.providerId}
                                    buttonText="Chat with Genie"
                                    className="w-full text-indigo-600 border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
                                    showIcon={true}
                                    variant="outline"
                                    size="sm"
                                  />
                                </div>

                                {/* Middle Column - Offer Details */}
                                <div className="lg:w-2/4 space-y-4 mt-4 lg:mt-0">
                                  <div>
                                    <div className="text-muted">
                                      {offer.description}
                                    </div>

                                    {offer.additionalDetails && (
                                      <div className="mt-3 p-3 bg-gray-50 rounded-md text-sm">
                                        <div className="font-medium text-heading mb-1">Additional Details:</div>
                                        {offer.additionalDetails}
                                      </div>
                                    )}
                                  </div>

                                  {/* Pricing and Completion Date */}
                                  <div className="grid grid-cols-2 gap-4 sm:gap-6 mt-4">
                                    <div>
                                      <div className="text-xs text-muted mb-1">Price</div>
                                      <div className="text-lg sm:text-xl font-bold text-heading">
                                        {getCurrencySymbol(offer.currency || (offer.will?.currency || 'ILS'))}
                                        {offer.price}
                                      </div>
                                    </div>

                                    <div>
                                      <div className="text-xs text-muted mb-1">Expected Completion</div>
                                      <div className="text-sm sm:text-base font-medium">
                                        {new Date(offer.estimatedCompletionDate).toLocaleDateString(undefined, {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric'
                                        })}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Progress bar */}
                                  <div className="pt-2">
                                    <div className="flex justify-between text-xs text-muted mb-1.5">
                                      <span>Progress</span>
                                      <span>{getProgressValue(offer.status)}%</span>
                                    </div>
                                    <div className="progress-elegant">
                                      <div
                                        className="progress-bar"
                                        style={{ width: `${getProgressValue(offer.status)}%` }}
                                      ></div>
                                    </div>
                                    <div className="flex justify-between text-xs text-muted mt-1.5">
                                      <span>Offered</span>
                                      <span>In Progress</span>
                                      <span>Completed</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Right Column - Actions */}
                                <div className="lg:w-1/4 space-y-4 flex flex-col justify-between mt-4 lg:mt-0">
                                  {/* View Details Button */}
                                  <Button
                                    onClick={() => openOfferDetails(offer)}
                                    variant="outline"
                                    className="w-full text-sm"
                                  >
                                    View Full Details
                                  </Button>

                                  {/* Action Buttons */}
                                  {offer.status === "PENDING" && (
                                    <div className="space-y-2 mt-2 sm:mt-0">
                                      <Button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStatusUpdate(offer.id, "IN_PROGRESS");
                                        }}
                                        className="w-full bg-green-600 hover:bg-green-700 text-white text-sm"
                                      >
                                        <Check className="h-4 w-4 mr-1" />
                                        Accept Offer
                                      </Button>

                                      <Button
                                        onClick={(e) => handleRejectClick(e, offer.id)}
                                        variant="outline"
                                        className="w-full text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700 text-sm"
                                      >
                                        <X className="h-4 w-4 mr-1" />
                                        Reject
                                      </Button>
                                    </div>
                                  )}

                                  {offer.status === "IN_PROGRESS" && (
                                    <Button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStatusUpdate(offer.id, "COMPLETED");
                                      }}
                                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm"
                                    >
                                      <BadgeCheck className="h-4 w-4 mr-1" />
                                      Mark as Completed
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Rejection Confirmation Dialog */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent className="max-w-md rounded-lg glass-effect p-0 overflow-hidden">
          <div className="px-6 pt-6">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-bold text-heading">Reject this offer?</AlertDialogTitle>
              <AlertDialogDescription className="text-muted">
                This action will reject the offer and cannot be undone. The provider will be notified of your decision.
              </AlertDialogDescription>
            </AlertDialogHeader>
          </div>
          <AlertDialogFooter className="p-6 border-t border-gray-100">
            <AlertDialogCancel className="rounded-md border-gray-200 bg-white hover:bg-gray-50 hover:text-gray-900">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (selectedOfferId) {
                  handleStatusUpdate(selectedOfferId, "REJECTED");
                }
                setShowRejectDialog(false);
              }}
              className="rounded-md bg-rose-600 hover:bg-rose-700 text-white"
            >
              Reject Offer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Offer Details Dialog */}
      <Dialog open={showOfferDetailsDialog} onOpenChange={setShowOfferDetailsDialog}>
        <DialogContent className="max-w-4xl w-[calc(100vw-32px)] h-[calc(100vh-120px)] overflow-y-auto p-0">
          {selectedOfferForView && (
            <div className="relative">
              {/* Dialog Close Button */}
              <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </DialogClose>
              
              <div className="p-6">
                {/* Offer Title and Status */}
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-heading">{selectedOfferForView.willTitle}</h2>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={`${
                      selectedOfferForView.status === "PENDING" ? "bg-amber-100 text-amber-800" :
                      selectedOfferForView.status === "IN_PROGRESS" ? "bg-blue-100 text-blue-800" :
                      selectedOfferForView.status === "COMPLETED" ? "bg-emerald-100 text-emerald-800" :
                      selectedOfferForView.status === "REJECTED" ? "bg-rose-100 text-rose-800" :
                      "bg-gray-100 text-gray-800"
                    }`}>
                      {statusIcons[selectedOfferForView.status as keyof typeof statusIcons]}
                      <span className="ml-1">{selectedOfferForView.status}</span>
                    </Badge>
                    <span className="text-sm text-gray-500">
                      Received {new Date(selectedOfferForView.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Provider Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Link to={`/profile/${selectedOfferForView.providerId}`} className="hover:opacity-80 transition-opacity">
                      <Avatar className="h-10 w-10 border-2 border-transparent hover:border-indigo-200 transition-all">
                        {getProviderProfilePicture(selectedOfferForView.providerId, selectedOfferForView) ? (
                          <AvatarImage 
                            src={getProviderProfilePicture(selectedOfferForView.providerId, selectedOfferForView) || ''} 
                            alt={selectedOfferForView.providerName} 
                          />
                        ) : (
                          <AvatarFallback className="bg-indigo-100 text-indigo-600">
                            {selectedOfferForView.providerName.charAt(0)}
                          </AvatarFallback>
                        )}
                      </Avatar>
                    </Link>
                    <div>
                      <Link 
                        to={`/profile/${selectedOfferForView.providerId}`}
                        className="font-medium text-heading hover:text-indigo-600 hover:underline transition-colors"
                      >
                        {selectedOfferForView.providerName}
                      </Link>
                      <div className="text-sm text-muted">{selectedOfferForView.providerContactInfo}</div>
                    </div>
                  </div>
                  <ChatButton
                    userId={selectedOfferForView.providerId}
                    buttonText="Chat with Genie"
                    className="w-full md:w-auto text-indigo-600 border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
                    showIcon={true}
                    variant="outline"
                    size="sm"
                  />
                </div>

                {/* Offer Description */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-heading mb-2">Description</h3>
                  <div className="text-gray-700">{selectedOfferForView.description}</div>
                  
                  {selectedOfferForView.additionalDetails && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-md">
                      <h4 className="font-medium text-heading mb-2">Additional Details:</h4>
                      <div className="text-gray-700">{selectedOfferForView.additionalDetails}</div>
                    </div>
                  )}
                </div>

                {/* Offer Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="space-y-1">
                    <div className="text-sm text-gray-500">Price</div>
                    <div className="text-xl font-bold text-heading">
                      {getCurrencySymbol(selectedOfferForView.currency || (selectedOfferForView.will?.currency || 'ILS'))}
                      {selectedOfferForView.price}
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="text-sm text-gray-500">Expected Completion</div>
                    <div className="text-base font-medium">
                      {new Date(selectedOfferForView.estimatedCompletionDate).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-sm text-gray-500">Current Status</div>
                    <div className="text-base font-medium flex items-center">
                      {statusIcons[selectedOfferForView.status as keyof typeof statusIcons]}
                      <span className="ml-1">{selectedOfferForView.status}</span>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-8">
                  <div className="flex justify-between text-xs text-muted mb-1.5">
                    <span>Progress</span>
                    <span>{getProgressValue(selectedOfferForView.status)}%</span>
                  </div>
                  <div className="progress-elegant">
                    <div
                      className="progress-bar"
                      style={{ width: `${getProgressValue(selectedOfferForView.status)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-muted mt-1.5">
                    <span>Offered</span>
                    <span>In Progress</span>
                    <span>Completed</span>
                  </div>
                </div>

                {/* Action Buttons */}
                {selectedOfferForView.status === "PENDING" && (
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStatusUpdate(selectedOfferForView.id, "IN_PROGRESS");
                        setShowOfferDetailsDialog(false);
                      }}
                      className="w-full sm:flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Accept Offer
                    </Button>

                    <Button
                      onClick={(e) => {
                        handleRejectClick(e, selectedOfferForView.id);
                        setShowOfferDetailsDialog(false);
                      }}
                      variant="outline"
                      className="w-full sm:flex-1 text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}

                {selectedOfferForView.status === "IN_PROGRESS" && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusUpdate(selectedOfferForView.id, "COMPLETED");
                      setShowOfferDetailsDialog(false);
                    }}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <BadgeCheck className="h-4 w-4 mr-1" />
                    Mark as Completed
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyGenies;
